import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import createCarousel from 'app/cmscomponents/carousel/mediaViewerCarousel/mediaViewerCarousel';
import mediaQueries from 'app/utils/mediaQueries';
import MediaItem from './mediaItem';
import MediaZoomModal from './mediaZoomModal';

const { imagesLimitCount, imagesBackground } = window.inlineProductDetails;

function MediaViewer({ productCode, imageSet }) {
  const carouselRef = useRef(null);
  const mediaViewerRef = useRef(null);
  const { items = [{ src: 'https://i8.amplience.net/i/fitflop/404?fmt=auto&qlt=default' }] } = imageSet;
  const [isLargeUp, setLocalIsLargeUp] = useState(mediaQueries.is_large_up());
  const [zoomedItemIndex, setZoomedItemIndex] = useState(null);

  const mediaSet = useMemo(() => {
    const trimmedMediaSet = imageFirst(excludeHeroUrls(items)).slice(0, imagesLimitCount);
    if (isLargeUp && imagesBackground) {
      return appendBackground(trimmedMediaSet);
    }
    return trimmedMediaSet;
  }, [isLargeUp, items]);

  useEffect(() => {
    const handleResize = debounce(() => {
      const currentIsLargeUp = mediaQueries.is_large_up();
      if (isLargeUp !== currentIsLargeUp) {
        setLocalIsLargeUp(currentIsLargeUp);
        setZoomedItemIndex(null);
      }
    }, 100);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isLargeUp]);

  useEffect(() => {
    const container = mediaViewerRef.current;
    if (!isLargeUp) {
      let frame;
      const buildWhenSized = () => {
        const mediaViewerEl = container.querySelector('.media-viewer');
        if (mediaViewerEl && mediaViewerEl.offsetWidth > 0) {
          carouselRef.current = createMediaCarousel(container);
          container.classList.add('media-viewer-container--carousel-ready');
        } else {
          frame = requestAnimationFrame(buildWhenSized);
        }
      };
      buildWhenSized();
      return () => cancelAnimationFrame(frame);
    }
    if (carouselRef.current) {
      carouselRef.current.destroyCarousel(true);
      container.classList.remove('media-viewer-container--carousel-ready');
    }
    return undefined;
  }, [isLargeUp, productCode]);

  useEffect(() => {
    const mediaViewerEl = mediaViewerRef.current;
    if (!mediaViewerEl) return undefined;

    const viewportObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (isLargeUp) {
            playAllVideos(mediaViewerEl);
          } else if (carouselRef.current) {
            playInViewVideo(carouselRef.current);
          }
        } else {
          pauseAllVideos(mediaViewerEl);
        }
      },
      { threshold: 0 }
    );

    viewportObserver.observe(mediaViewerEl);

    return () => viewportObserver.disconnect();
  }, [isLargeUp]);

  const openZoom = index => {
    pauseAllVideos(mediaViewerRef.current);
    setZoomedItemIndex(index);
  };

  const closeZoom = useCallback(() => {
    setZoomedItemIndex(null);
    playAllVideos(mediaViewerRef.current);
  }, []);

  return (
    <div className="media-viewer-container" key={`media-viewer_${productCode}`} ref={mediaViewerRef}>
      <div className="media-viewer">
        {mediaSet.map((item, index) => (
          <div className="media-viewer__item" key={`${item.src}_${index + 1}`}>
            {isLargeUp ? (
              <button type="button" className="media-viewer__zoom-trigger" onClick={() => openZoom(index)}>
                <MediaItem item={item} isZoomable={false} />
              </button>
            ) : (
              <MediaItem item={item} />
            )}
          </div>
        ))}
      </div>
      {isLargeUp && zoomedItemIndex !== null && (
        <MediaZoomModal items={mediaSet} startIndex={zoomedItemIndex} onClose={closeZoom} />
      )}
    </div>
  );
}

function excludeHeroUrls(items) {
  const heroUrls = ['_hero43', '_hero169'];
  return items.filter(item => !heroUrls.some(heroUrl => item.src.includes(heroUrl)));
}

function imageFirst(items) {
  if (items[0] && items[0].type !== 'video') {
    return items;
  }
  return [...items.filter(item => item.type !== 'video'), ...items.filter(item => item.type === 'video')];
}

function appendBackground(mediaSet) {
  mediaSet.map(item => {
    if (item.type === 'img') {
      const imageSrc = new URL(item.src);
      imageSrc.searchParams.append('bg', imagesBackground);
      return {
        ...item,
        src: imageSrc.toString(),
      };
    }
    return item;
  });
}

function createMediaCarousel(mediaViewerEl) {
  const carousel = createCarousel(mediaViewerEl.querySelector('.media-viewer'), {
    onResize: () => {
      pauseAllVideos(mediaViewerEl);
      playInViewVideo(carousel);
    },
    onChange: () => {
      pauseAllVideos(mediaViewerEl);
      playInViewVideo(carousel);
    },
  });
  pauseAllVideos(mediaViewerEl);
  playInViewVideo(carousel);
  return carousel;
}

function playAllVideos(mediaViewerEl) {
  [...mediaViewerEl.querySelectorAll('.video')].forEach(video => {
    video.setAttribute('muted', true);
    video.play();
  });
}

function pauseAllVideos(mediaViewerEl) {
  [...mediaViewerEl.querySelectorAll('.video')].forEach(video => {
    video.pause();
  });
}

function playInViewVideo(carousel) {
  const video = carousel.innerElements[carousel.currentSlide].querySelector('video');
  if (video) {
    video.setAttribute('muted', true);
    video.play();
  }
}

MediaViewer.propTypes = {
  productCode: PropTypes.string.isRequired,
  imageSet: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.string])).isRequired,
};

export default MediaViewer;
